import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { getRoomURL } from '@src/helpers/rooms.helpers';
import { useRoomActionsStore } from '@src/stores';
import { useRoomContext } from '@src/contexts';

const COPY_RESET_DELAY = 2000;

const useShareRoomModal = (shareButtonRef: RefObject<HTMLDivElement> | null) => {
  const { isMobileDevice } = useRoomContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const meetingLink = getRoomURL();

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
    setIsCopied(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsCopied(false);
  }, []);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(meetingLink);
    setIsCopied(true);

    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setIsCopied(false), COPY_RESET_DELAY);
  }, [meetingLink]);

  const handleSocialClick = useCallback(
    (getUrl: (link: string) => string) => {
      window.open(getUrl(meetingLink), '_blank', 'noopener,noreferrer');
    },
    [meetingLink]
  );

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!shareButtonRef?.current) return;
    if ('share' in navigator) return;

    useRoomActionsStore.setState((state) => ({
      shareRoomSettings: {
        ...state.shareRoomSettings,
        onClickCallback: () => handleToggle(),
      },
    }));
  }, [handleToggle, shareButtonRef]);

  const isSupported = !!shareButtonRef?.current && !('share' in navigator);

  return {
    isOpen,
    isCopied,
    isMobileDevice,
    meetingLink,
    isSupported,
    handleClose,
    handleCopyLink,
    handleSocialClick,
  };
};

export default useShareRoomModal;
